import {
  DndContext,
  type DragEndEvent,
  type DragOverEvent,
  DragOverlay,
  type DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { css, cva } from '@styled-system/css';
import {
  ChevronDown,
  ChevronRight,
  CornerDownRight,
  Eye,
  Folder as FolderIcon,
  FolderInput,
  GripVertical,
  MoreHorizontal,
  Pencil,
  Plus,
  Search,
  Star,
  Trash2,
} from 'lucide-react';
import { type FC, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AnalysisItemData } from '@/features/analysisMenu/types';
import { PlatformBadge } from '@/shared/components/analysis/PlatformBadge';
import { Popover } from '@/shared/components/ui/Popover';
import { Tooltip } from '@/shared/components/ui/Tooltip';
import { useDeleteAnalysis } from '@/shared/hooks/analysis/mutations';
import { useIsMobile } from '@/shared/hooks/useIsMobile';
import { isVestiaireAnalysis } from '@/shared/utils/analysis/platform';
import { getAnalysisIntent } from '@/shared/utils/analysis/searchIntent';

import { type SearchFolder, type SearchOrganizationActions } from '../organization/useSearchOrganizationServer';

import { MoveSearchModal } from './MoveSearchModal';

// ============================================================================
// Historique organisé en dossiers — même DA que la page Filtres d'Autocop
// (FolderSection + FolderHeader + dnd-kit) : morph live du dossier pendant le
// drag, drop sur une ligne = insertion à sa position, drop sur la zone du
// dossier = ajout à la fin. Le favori est un état (étoile), indépendant du
// dossier. Les recherches lancées depuis une niche s'affichent sous leur
// recherche mère ; les saisir et les déposer ailleurs casse le lien.
// ============================================================================

const UNFILED_DROP_ID = '__unfiled__';

// ─── Sections (DA Autocop) ─────────────────────────────────────────────────

const sectionWrapper = cva({
  base: {
    marginBottom: '12',
    paddingY: '4',
    paddingX: '4',
    borderRadius: 'xl',
    transition: 'background 120ms ease, outline-color 120ms ease',
    outlineWidth: '1px',
    outlineStyle: 'dashed',
    outlineColor: 'transparent',
    outlineOffset: '-4px',
  },
  variants: {
    isOver: {
      true: { bg: 'brand.primarySoft', outlineColor: 'brand.primary' },
      false: {},
    },
  },
});

const sectionHeader = css({
  display: 'flex',
  alignItems: 'center',
  gap: '5',
  paddingY: '5',
  paddingX: '8',
  marginBottom: '5',
  cursor: 'pointer',
  userSelect: 'none',
  bg: 'surface.subtle',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.default',
  borderRadius: 'lg',
  transition: 'border-color 120ms ease, background 120ms ease',
  _hover: { borderColor: 'border.strong' },
});

const chevron = css({
  color: 'text.tertiary',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: '24px',
  height: '24px',
});

const sectionIcon = css({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'text.tertiary',
  flexShrink: 0,
});

const sectionName = css({
  fontSize: 'body',
  fontWeight: 'semibold',
  color: 'text.primary',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const countBadge = css({
  fontFamily: 'mono',
  fontSize: 'micro',
  paddingY: '1',
  paddingX: '3',
  borderRadius: 'xs',
  bg: 'surface.sunken',
  color: 'text.tertiary',
  flexShrink: 0,
});

const actionsRow = css({
  display: 'flex',
  alignItems: 'center',
  gap: '2',
  flexShrink: 0,
});

const dotsAction = css({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '30px',
  height: '30px',
  bg: 'surface.default',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.default',
  borderRadius: 'sm',
  color: 'text.secondary',
  cursor: 'pointer',
  boxShadow: 'xs',
  transition: 'background 120ms ease, border-color 120ms ease, color 120ms ease',
  _hover: { bg: 'surface.subtle', borderColor: 'border.strong', color: 'text.primary' },
});

const menu = css({ display: 'flex', flexDirection: 'column', gap: '1', minWidth: '180px' });

const menuItem = css({
  display: 'flex',
  alignItems: 'center',
  gap: '4',
  paddingY: '4',
  paddingX: '5',
  fontSize: 'bodySm',
  color: 'text.primary',
  borderRadius: 'sm',
  cursor: 'pointer',
  background: 'transparent',
  border: 'none',
  width: '100%',
  textAlign: 'left',
  _hover: { bg: 'surface.sunken' },
});

const menuItemDanger = css({ color: 'error.default', _hover: { bg: 'error.soft' } });

const menuDivider = css({ height: '1px', bg: 'border.subtle', marginY: '2' });

const emptySection = css({
  paddingY: '8',
  paddingX: '4',
  textAlign: 'center',
  fontSize: 'bodySm',
  color: 'text.tertiary',
  borderWidth: '1px',
  borderStyle: 'dashed',
  borderColor: 'border.default',
  borderRadius: 'lg',
});

// ─── Lignes ────────────────────────────────────────────────────────────────

const list = css({ display: 'flex', flexDirection: 'column', gap: '2' });

const row = cva({
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: '4',
    paddingY: '4',
    paddingX: '5',
    bg: 'surface.default',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'border.default',
    borderRadius: 'lg',
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left',
    transition: 'background 120ms ease, border-color 120ms ease, box-shadow 120ms ease, opacity 120ms ease',
    _hover: { borderColor: 'border.strong', boxShadow: 'xs', bg: 'surface.subtle' },
  },
  variants: {
    dragging: {
      true: { opacity: 0.35 },
      false: {},
    },
    child: {
      true: { bg: 'surface.subtle', borderStyle: 'dashed' },
      false: {},
    },
  },
});

const dragHandle = css({
  display: 'inline-flex',
  alignItems: 'center',
  color: 'text.tertiary',
  flexShrink: 0,
  touchAction: 'none',
});

const rowThumb = css({
  width: '36px',
  height: '36px',
  borderRadius: 'md',
  bg: 'surface.sunken',
  objectFit: 'cover',
  flexShrink: 0,
});

// Recherche vendeur : `imageUrl` est l'AVATAR du vendeur (API, 2026-10-01), rendu rond comme tout avatar.
const rowAvatar = css({
  width: '36px',
  height: '36px',
  borderRadius: 'full',
  bg: 'surface.sunken',
  objectFit: 'cover',
  flexShrink: 0,
});

const thumbClassFor = (analysis: AnalysisItemData) => (analysis.type === 'vendor' ? rowAvatar : rowThumb);

const rowThumbFallback = css({
  width: '36px',
  height: '36px',
  borderRadius: 'md',
  bg: 'surface.sunken',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'text.tertiary',
  flexShrink: 0,
});

const rowMain = css({ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '1' });

const rowNameLine = css({ display: 'flex', alignItems: 'center', gap: '2', minWidth: 0 });

const rowName = css({
  flex: 1,
  minWidth: 0,
  fontSize: 'body',
  fontWeight: 'medium',
  color: 'text.primary',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const rowMeta = css({ fontSize: 'caption', color: 'text.tertiary' });

const intentChip = cva({
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    paddingY: '0.5',
    paddingX: '3',
    borderRadius: 'full',
    fontSize: 'micro',
    fontWeight: 'medium',
    borderWidth: '1px',
    borderStyle: 'solid',
    flexShrink: 0,
  },
  variants: {
    tone: {
      discovery: { bg: 'info.soft', color: 'info.default', borderColor: 'info.border' },
      targeted: { bg: 'surface.sunken', color: 'text.tertiary', borderColor: 'border.default' },
    },
  },
});

const starButton = cva({
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    background: 'transparent',
    border: 'none',
    borderRadius: 'sm',
    cursor: 'pointer',
    color: 'text.tertiary',
    flexShrink: 0,
    transition: 'color 120ms ease, background 120ms ease',
    _hover: { bg: 'surface.sunken', color: 'warning.default' },
  },
  variants: {
    active: {
      true: { color: 'warning.default' },
      false: {},
    },
  },
});

// Menu ⋯ d'une ligne — même gabarit discret que l'étoile
const rowDotsButton = css({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '28px',
  height: '28px',
  background: 'transparent',
  border: 'none',
  borderRadius: 'sm',
  cursor: 'pointer',
  color: 'text.tertiary',
  flexShrink: 0,
  transition: 'color 120ms ease, background 120ms ease',
  _hover: { bg: 'surface.sunken', color: 'text.primary' },
});

const childIndent = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '2',
  paddingLeft: '12',
});

const childIcon = css({
  color: 'text.tertiary',
  flexShrink: 0,
});

const dragGhost = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4',
  paddingY: '3',
  paddingX: '5',
  borderRadius: 'lg',
  bg: 'surface.elevated',
  color: 'text.primary',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.default',
  boxShadow: 'lg',
  fontSize: 'bodySm',
  cursor: 'grabbing',
  transform: 'rotate(-0.5deg)',
  width: '340px',
  maxWidth: 'calc(100vw - 48px)',
});

// ============================================================================

interface SearchRowProps {
  analysis: AnalysisItemData;
  meta: string;
  isChild?: boolean;
  draggingId: string | null;
  onOpen: (id: string) => void;
  onToggleFavorite: (id: string) => void;
  onRenameSearch: (analysis: AnalysisItemData) => void;
  onViewSearch?: (analysis: AnalysisItemData) => void;
  onMoveSearch: (analysis: AnalysisItemData) => void;
  onDeleteSearch: (analysis: AnalysisItemData) => void;
}

const SearchRow: FC<SearchRowProps> = ({
  analysis,
  meta,
  isChild = false,
  draggingId,
  onOpen,
  onToggleFavorite,
  onRenameSearch,
  onViewSearch,
  onMoveSearch,
  onDeleteSearch,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const intent = analysis.pageUrl || isVestiaireAnalysis(analysis) ? getAnalysisIntent(analysis) : null;
  const isFavorite = analysis.isFavorite ?? false;

  // Les filles sont draggables aussi : les saisir et les déposer ailleurs
  // casse le lien avec la mère (géré au dragEnd).
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: analysis.id,
    data: { type: 'search', folderId: analysis.folderId ?? null },
  });

  return (
    <div
      ref={setNodeRef}
      className={row({ dragging: isDragging || draggingId === analysis.id, child: isChild })}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      onClick={() => onOpen(analysis.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter') onOpen(analysis.id);
      }}
    >
      {!isMobile && (
        <span
          ref={setActivatorNodeRef}
          className={dragHandle}
          {...attributes}
          {...listeners}
          onClick={(event) => event.stopPropagation()}
          style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        >
          <GripVertical size={14} strokeWidth={1.75} />
        </span>
      )}
      {isChild && <CornerDownRight size={14} strokeWidth={1.75} className={childIcon} />}

      {analysis.imageUrl ? (
        <img src={analysis.imageUrl} alt="" className={thumbClassFor(analysis)} />
      ) : (
        <span className={rowThumbFallback}>
          <Search size={15} strokeWidth={1.75} />
        </span>
      )}

      <span className={rowMain}>
        <span className={rowNameLine}>
          <span className={rowName}>{analysis.name}</span>
        </span>
        {meta && <span className={rowMeta}>{meta}</span>}
      </span>

      {/* Plateforme : élément de la ligne (centré verticalement comme l'étiquette de niche), pas collé au nom */}
      <PlatformBadge platform={analysis.platform} />

      {intent && (
        <span className={intentChip({ tone: intent })}>
          {intent === 'discovery' ? t('productSearch.intentDiscovery') : t('productSearch.intentTargeted')}
        </span>
      )}

      {/* Favori = état serveur, indépendant du dossier */}
      <button
        type="button"
        className={starButton({ active: isFavorite })}
        aria-label={isFavorite ? t('folders.favoriteRemove') : t('folders.favoriteAdd')}
        onClick={(event) => {
          event.stopPropagation();
          onToggleFavorite(analysis.id);
        }}
      >
        <Star size={14} strokeWidth={1.75} fill={isFavorite ? 'currentColor' : 'none'} />
      </button>

      {/* Menu ⋯ : actions de la recherche */}
      <span onClick={(event) => event.stopPropagation()}>
        <Popover
          placement="bottom-end"
          trigger={
            <button type="button" className={rowDotsButton} aria-label={t('folders.actions')}>
              <MoreHorizontal size={15} strokeWidth={1.75} />
            </button>
          }
        >
          <div className={menu}>
            <button type="button" className={menuItem} onClick={() => onRenameSearch(analysis)}>
              <Pencil size={14} strokeWidth={1.75} />
              {t('folders.rename')}
            </button>
            {onViewSearch && (
              <button type="button" className={menuItem} onClick={() => onViewSearch(analysis)}>
                <Eye size={14} strokeWidth={1.75} />
                {t('folders.viewFilters')}
              </button>
            )}
            <button type="button" className={menuItem} onClick={() => onMoveSearch(analysis)}>
              <FolderInput size={14} strokeWidth={1.75} />
              {t('folders.changeFolder')}
            </button>
            <div className={menuDivider} />
            <button type="button" className={`${menuItem} ${menuItemDanger}`} onClick={() => onDeleteSearch(analysis)}>
              <Trash2 size={14} strokeWidth={1.75} />
              {t('folders.delete')}
            </button>
          </div>
        </Popover>
      </span>

      <ChevronRight size={16} strokeWidth={1.75} className={css({ color: 'text.tertiary', flexShrink: 0 })} />
    </div>
  );
};

// ─── Section (dossier) — header façon Autocop ──────────────────────────────

interface FolderSectionProps {
  folder: SearchFolder | null;
  folderKey: string | null;
  name: string;
  searches: AnalysisItemData[];
  childrenByParent: Map<string, AnalysisItemData[]>;
  buildMeta: (analysis: AnalysisItemData) => string;
  draggingId: string | null;
  isFiltering: boolean;
  onOpen: (id: string) => void;
  onToggleFavorite: (id: string) => void;
  onCreateSearch: (folderId: string | null) => void;
  onRename?: (folder: SearchFolder) => void;
  onDelete?: (folder: SearchFolder) => void;
  onRenameSearch: (analysis: AnalysisItemData) => void;
  onViewSearch?: (analysis: AnalysisItemData) => void;
  onMoveSearch: (analysis: AnalysisItemData) => void;
  onDeleteSearch: (analysis: AnalysisItemData) => void;
}

const HistoryFolderSection: FC<FolderSectionProps> = ({
  folder,
  folderKey,
  name,
  searches,
  childrenByParent,
  buildMeta,
  draggingId,
  isFiltering,
  onOpen,
  onToggleFavorite,
  onCreateSearch,
  onRename,
  onDelete,
  onRenameSearch,
  onViewSearch,
  onMoveSearch,
  onDeleteSearch,
}) => {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);

  const { isOver, setNodeRef } = useDroppable({
    id: folderKey ?? UNFILED_DROP_ID,
    data: { type: 'folder', folderId: folderKey },
  });

  // En filtrage (recherche/favoris), les sections sans résultat disparaissent
  if (isFiltering && searches.length === 0) return null;

  return (
    <div ref={setNodeRef} className={sectionWrapper({ isOver })}>
      <div
        role="button"
        tabIndex={0}
        className={sectionHeader}
        onClick={() => setCollapsed((c) => !c)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setCollapsed((c) => !c);
          }
        }}
      >
        <span className={chevron}>
          {collapsed ? <ChevronRight size={16} strokeWidth={2} /> : <ChevronDown size={16} strokeWidth={2} />}
        </span>
        {folder && (
          <span className={sectionIcon}>
            <FolderIcon size={15} strokeWidth={1.75} />
          </span>
        )}
        <span className={sectionName}>{name}</span>
        <span className={countBadge}>{searches.length}</span>
        <div className={css({ flex: 1 })} />
        <div className={actionsRow} onClick={(event) => event.stopPropagation()}>
          <Tooltip label={t('folders.addSearch')}>
            <button
              type="button"
              className={dotsAction}
              aria-label={t('folders.addSearch')}
              onClick={() => onCreateSearch(folderKey)}
            >
              <Plus size={14} strokeWidth={1.75} />
            </button>
          </Tooltip>
          {folder && onRename && onDelete && (
            <Popover
              placement="bottom-end"
              trigger={
                <button type="button" className={dotsAction} aria-label={t('folders.actions')}>
                  <MoreHorizontal size={14} strokeWidth={1.75} />
                </button>
              }
            >
              <div className={menu}>
                <button type="button" className={menuItem} onClick={() => onRename(folder)}>
                  <Pencil size={14} strokeWidth={1.75} />
                  {t('folders.rename')}
                </button>
                <div className={menuDivider} />
                <button type="button" className={`${menuItem} ${menuItemDanger}`} onClick={() => onDelete(folder)}>
                  <Trash2 size={14} strokeWidth={1.75} />
                  {t('folders.delete')}
                </button>
              </div>
            </Popover>
          )}
        </div>
      </div>

      {!collapsed && searches.length === 0 && <div className={emptySection}>{t('folders.empty')}</div>}

      {!collapsed && searches.length > 0 && (
        <div className={list}>
          {searches.map((analysis) => {
            const children = childrenByParent.get(analysis.id) ?? [];
            return (
              <div key={analysis.id} className={css({ display: 'flex', flexDirection: 'column', gap: '2' })}>
                <SearchRow
                  analysis={analysis}
                  meta={buildMeta(analysis)}
                  draggingId={draggingId}
                  onOpen={onOpen}
                  onToggleFavorite={onToggleFavorite}
                  onRenameSearch={onRenameSearch}
                  onViewSearch={onViewSearch}
                  onMoveSearch={onMoveSearch}
                  onDeleteSearch={onDeleteSearch}
                />
                {/* Recherches lancées depuis une niche de cette recherche :
                    la mère fait office de catégorie. */}
                {children.length > 0 && (
                  <div className={childIndent}>
                    {children.map((child) => (
                      <SearchRow
                        key={child.id}
                        analysis={child}
                        meta={buildMeta(child)}
                        isChild
                        draggingId={draggingId}
                        onOpen={onOpen}
                        onToggleFavorite={onToggleFavorite}
                        onRenameSearch={onRenameSearch}
                        onViewSearch={onViewSearch}
                        onMoveSearch={onMoveSearch}
                        onDeleteSearch={onDeleteSearch}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Orchestration DnD ─────────────────────────────────────────────────────

export interface SearchHistoryFoldersProps {
  analyses: AnalysisItemData[];
  buildMeta: (analysis: AnalysisItemData) => string;
  /** Dossiers serveur (getUserSearchFolders) + actions de mutation. */
  folders: SearchFolder[];
  actions: SearchOrganizationActions;
  /** Filtre texte (nom de la recherche). */
  filterText?: string;
  /** N'affiche que les recherches favorites. */
  favoritesOnly?: boolean;
  onOpen: (id: string) => void;
  onRenameFolder: (folder: SearchFolder) => void;
  /** Ouvre la popup de création pré-affectée au dossier. */
  onCreateSearch: (folderId: string | null) => void;
  /** Menu ⋯ d'une recherche : renommer. */
  onRenameSearch: (analysis: AnalysisItemData) => void;
  /** Menu ⋯ : voir les filtres de la recherche (optionnel — produit). */
  onViewSearch?: (analysis: AnalysisItemData) => void;
}

export const SearchHistoryFolders: FC<SearchHistoryFoldersProps> = ({
  analyses,
  buildMeta,
  folders,
  actions,
  filterText = '',
  favoritesOnly = false,
  onOpen,
  onRenameFolder,
  onCreateSearch,
  onRenameSearch,
  onViewSearch,
}) => {
  const { t } = useTranslation();
  const [draggingId, setDraggingId] = useState<string | null>(null);
  // Dossier d'origine au drag-start — le morph live change le placement en
  // cours de drag, il faut l'original pour décider same-folder vs cross-folder.
  const dragOriginRef = useRef<string | null | undefined>(undefined);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor),
  );

  const isFiltering = filterText.trim().length > 0 || favoritesOnly;

  // Filtres de vue (texte + favoris) appliqués avant le groupement
  const visibleAnalyses = useMemo(() => {
    let result = analyses;
    if (favoritesOnly) result = result.filter((a) => a.isFavorite);
    if (filterText.trim()) {
      const query = filterText.trim().toLowerCase();
      result = result.filter((a) => a.name.toLowerCase().includes(query));
    }
    return result;
  }, [analyses, favoritesOnly, filterText]);

  const byId = useMemo(() => new Map(visibleAnalyses.map((a) => [a.id, a])), [visibleAnalyses]);

  // Remonte la chaîne mère→fille jusqu'à l'ancêtre racine présent dans la
  // liste (une fille de fille s'affiche sous la recherche d'origine, à plat).
  // Garde-fou sur les cycles. Un DOSSIER explicitement choisi est PRIORITAIRE
  // sur la lignée : la fille s'affiche alors dans son dossier, pas sous la
  // mère (cas du dossier sélectionné dans le popup de relance).
  const resolveRoot = useMemo(() => {
    return (id: string): string | null => {
      if ((byId.get(id)?.folderId ?? null) !== null) return null;
      let current = id;
      const visited = new Set<string>([id]);
      let parentId = byId.get(current)?.parentAnalysisId ?? null;
      while (parentId && byId.has(parentId) && !visited.has(parentId)) {
        visited.add(parentId);
        current = parentId;
        parentId = byId.get(current)?.parentAnalysisId ?? null;
      }
      return current === id ? null : current;
    };
  }, [byId]);

  // Descendants groupés sous leur ancêtre racine (un seul niveau d'indentation)
  const childrenByParent = useMemo(() => {
    const result = new Map<string, AnalysisItemData[]>();
    for (const analysis of visibleAnalyses) {
      const rootId = resolveRoot(analysis.id);
      if (rootId) {
        const arr = result.get(rootId);
        if (arr) arr.push(analysis);
        else result.set(rootId, [analysis]);
      }
    }
    return result;
  }, [visibleAnalyses, resolveRoot]);

  const topLevel = useMemo(
    () => visibleAnalyses.filter((a) => resolveRoot(a.id) === null),
    [visibleAnalyses, resolveRoot],
  );

  // Tri par position dense serveur ; null = jamais déplacée → après les
  // positionnées, ordre d'arrivée (createdAt desc) préservé.
  const searchesInFolder = (folderId: string | null): AnalysisItemData[] =>
    topLevel
      .filter((a) => (a.folderId ?? null) === folderId)
      .sort((a, b) => (a.position ?? Number.MAX_SAFE_INTEGER) - (b.position ?? Number.MAX_SAFE_INTEGER));

  const unfiledSearches = searchesInFolder(null);

  // Ordre global des ids (dossiers → sans dossier, chaque mère suivie de ses
  // filles) pour le SortableContext de page — permet l'animation cross-folder
  // et le drag des filles.
  const allIdsInOrder = useMemo(() => {
    const ids: string[] = [];
    const pushWithChildren = (analysisList: AnalysisItemData[]) => {
      for (const analysis of analysisList) {
        ids.push(analysis.id);
        for (const child of childrenByParent.get(analysis.id) ?? []) ids.push(child.id);
      }
    };
    for (const folder of folders) pushWithChildren(searchesInFolder(folder.id));
    pushWithChildren(unfiledSearches);
    return ids;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topLevel, folders, childrenByParent]);

  const folderOf = (id: string): string | null => byId.get(id)?.folderId ?? null;

  const handleDragStart = (event: DragStartEvent) => {
    const id = String(event.active.id);
    dragOriginRef.current = folderOf(id);
    setDraggingId(id);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) return;
    const activeId = String(active.id);
    if (activeId === String(over.id)) return;

    const overData = over.data.current as { type?: 'search' | 'folder'; folderId?: string | null } | undefined;

    let targetFolderId: string | null;
    if (overData?.type === 'search') {
      targetFolderId = folderOf(String(over.id));
    } else if (overData?.type === 'folder') {
      targetFolderId = overData.folderId ?? null;
    } else {
      return;
    }

    if (folderOf(activeId) === targetFolderId) return;
    actions.morphFolder(activeId, targetFolderId);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setDraggingId(null);
    const originFolderId = dragOriginRef.current;
    dragOriginRef.current = undefined;

    const searchId = String(event.active.id);
    if (!event.over) return;

    // Recherche fille déposée quelque part : le lien avec la mère est cassé,
    // elle redevient une recherche normale et atterrit là où on la lâche.
    const wasChild = Boolean(byId.get(searchId)?.parentAnalysisId);
    if (wasChild) void actions.removeParent(searchId);

    const targetData = event.over.data.current as { type?: 'search' | 'folder'; folderId?: string | null } | undefined;

    // ─── Drop sur une LIGNE → insertion à sa position ────────────────────
    if (targetData?.type === 'search') {
      const targetId = String(event.over.id);

      // Self-drop après morph cross-folder (seul sortable du dossier cible)
      if (targetId === searchId) {
        const morphedFolderId = folderOf(searchId);
        if (wasChild || morphedFolderId !== originFolderId) {
          void actions.moveSearch(searchId, morphedFolderId);
        }
        return;
      }

      const targetFolderId = folderOf(targetId);
      const targetSiblings = topLevel
        .filter((a) => folderOf(a.id) === targetFolderId && a.id !== searchId)
        .sort((a, b) => (a.position ?? Number.MAX_SAFE_INTEGER) - (b.position ?? Number.MAX_SAFE_INTEGER));
      const overIndex = targetSiblings.findIndex((a) => a.id === targetId);
      // Sens du drop : la carte lâchée SOUS le milieu de la ligne cible
      // s'insère APRÈS elle (l'ancien calcul insérait toujours avant, d'où
      // les cartes qui n'atterrissaient pas là où on les lâchait).
      const activeTop = event.active.rect.current.translated?.top;
      const overRect = event.over.rect;
      const insertAfter = activeTop != null && activeTop > overRect.top + overRect.height / 2;
      const newIndex = overIndex < 0 ? undefined : overIndex + (insertAfter ? 1 : 0);
      void actions.moveSearch(searchId, targetFolderId, newIndex);
      return;
    }

    // ─── Drop sur la zone d'un DOSSIER ───────────────────────────────────
    if (targetData?.type === 'folder') {
      const targetFolderId = targetData.folderId ?? null;
      // Une ex-fille n'a pas de placement fiable : toujours un vrai move
      if (wasChild || originFolderId !== targetFolderId) {
        void actions.moveSearch(searchId, targetFolderId);
        return;
      }
      // Same-folder : premier ou dernier selon le sens du drag
      const siblings = searchesInFolder(targetFolderId);
      const sourceIndex = siblings.findIndex((a) => a.id === searchId);
      const draggedDown = (event.delta?.y ?? 0) > 0;
      const targetIndex = draggedDown ? siblings.length - 1 : 0;
      if (sourceIndex !== targetIndex && sourceIndex >= 0) {
        void actions.moveSearch(searchId, targetFolderId, targetIndex);
      }
    }
  };

  const draggingAnalysis = draggingId ? byId.get(draggingId) : null;

  const handleToggleFavorite = (id: string) => {
    void actions.toggleFavorite(id);
  };

  const handleDeleteFolder = (folder: SearchFolder) => {
    if (window.confirm(t('folders.deleteConfirm', { name: folder.name }))) {
      void actions.deleteFolder(folder.id);
    }
  };

  const { deleteAnalysis } = useDeleteAnalysis();
  const handleDeleteSearch = (analysis: AnalysisItemData) => {
    if (window.confirm(t('folders.deleteSearchConfirm', { name: analysis.name }))) {
      void deleteAnalysis(analysis.id);
    }
  };

  // Popup « Changer de dossier » (menu ⋯)
  const [moveTarget, setMoveTarget] = useState<AnalysisItemData | null>(null);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={allIdsInOrder} strategy={verticalListSortingStrategy}>
        <div>
          {folders.map((folder) => (
            <HistoryFolderSection
              key={folder.id}
              folder={folder}
              folderKey={folder.id}
              name={folder.name}
              searches={searchesInFolder(folder.id)}
              childrenByParent={childrenByParent}
              buildMeta={buildMeta}
              draggingId={draggingId}
              isFiltering={isFiltering}
              onOpen={onOpen}
              onToggleFavorite={handleToggleFavorite}
              onCreateSearch={onCreateSearch}
              onRename={onRenameFolder}
              onDelete={handleDeleteFolder}
              onRenameSearch={onRenameSearch}
              onViewSearch={onViewSearch}
              onMoveSearch={(analysis) => setMoveTarget(analysis)}
              onDeleteSearch={handleDeleteSearch}
            />
          ))}

          <HistoryFolderSection
            folder={null}
            folderKey={null}
            name={t('folders.unfiled')}
            searches={unfiledSearches}
            childrenByParent={childrenByParent}
            buildMeta={buildMeta}
            draggingId={draggingId}
            isFiltering={isFiltering}
            onOpen={onOpen}
            onToggleFavorite={handleToggleFavorite}
            onCreateSearch={onCreateSearch}
            onRenameSearch={onRenameSearch}
            onViewSearch={onViewSearch}
            onMoveSearch={(analysis) => setMoveTarget(analysis)}
            onDeleteSearch={handleDeleteSearch}
          />
        </div>
      </SortableContext>

      {moveTarget && (
        <MoveSearchModal
          isOpen
          searchName={moveTarget.name}
          folders={folders}
          currentFolderId={moveTarget.folderId ?? null}
          onClose={() => setMoveTarget(null)}
          onMove={(folderId) => void actions.moveSearch(moveTarget.id, folderId)}
        />
      )}

      <DragOverlay dropAnimation={null}>
        {draggingAnalysis ? (
          <div className={dragGhost}>
            {draggingAnalysis.imageUrl ? (
              <img src={draggingAnalysis.imageUrl} alt="" className={thumbClassFor(draggingAnalysis)} />
            ) : (
              <span className={rowThumbFallback}>
                <Search size={15} strokeWidth={1.75} />
              </span>
            )}
            <span className={rowName}>{draggingAnalysis.name}</span>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};
